/**
 * Sidebar / bottom app bar for shop owner dashboard (matches client tray design).
 */
import React, { memo, useMemo, useState } from 'react';
import { FaUser, FaCalendarAlt, FaClipboardList, FaChartBar, FaSignOutAlt, FaChevronLeft } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { prepareNewBookingSound } from '../../utils/newBookingSound';
import { makeNav } from './utils';
import ELogo from '../ELogo';

const NAV_ICONS = {
  profile: FaUser,
  calendar: FaCalendarAlt,
  history: FaClipboardList,
  analytics: FaChartBar,
  logout: FaSignOutAlt,
};

const NavButton = memo(({ item, active, count, onClick }) => {
  const { key, title, icon: IconComponent } = item;
  const isLogout = key === 'logout';
  const [hot, setHot] = useState(false);

  return (
    <button
      className={`nav-btn ${isLogout ? 'logout' : ''} ${active === key ? 'active' : ''} ${hot ? 'is-hot' : ''}`}
      onClick={onClick}
      onPointerEnter={() => setHot(true)}
      onPointerLeave={() => setHot(false)}
      onPointerDown={() => setHot(true)}
      onPointerUp={() => setHot(false)}
      onPointerCancel={() => setHot(false)}
      aria-current={active === key ? 'page' : undefined}
      aria-label={count > 0 ? `${title} (${count} pending)` : title}
      title={title}
      type="button"
    >
      <span className="nav-icon-container" style={{ position: 'relative' }}>
        {IconComponent && <IconComponent className="nav-img" aria-hidden="true" />}
        {count > 0 && (
          <span className="nav-badge" aria-label={`${count} pending`}>
            {count > 99 ? '99+' : String(count)}
          </span>
        )}
      </span>
      <span className="nav-title">{title}</span>
    </button>
  );
});

NavButton.displayName = 'NavButton';

const Sidebar = memo(function Sidebar({
  tab,
  setTab,
  logout,
  isOpen,
  onClose,
  pendingCount = 0,
  badgeCounts,
}) {
  const { t } = useI18n();
  const navItems = makeNav(t, NAV_ICONS);
  const counts = badgeCounts || { history: pendingCount };

  const { items, logoutItem } = useMemo(() => {
    const logoutNav = navItems.find((item) => item.key === 'logout') || null;
    const rest = navItems.filter((item) => item.key !== 'logout');
    return { items: rest, logoutItem: logoutNav };
  }, [navItems]);

  return (
    <nav
      className={`nav-container nav--owner nav--tray ${isOpen ? '' : 'sidebar-closed'}`}
      aria-label="Primary"
    >
      <div className="sidebar-title sidebar-title--tray">
        <div className="sidebar-logo-plate">
          <ELogo size="medium" />
        </div>
      </div>

      <div className="nav-tray">
        <div className="nav-items">
          {items.map((item) => (
            <NavButton
              key={item.key}
              item={item}
              active={tab}
              count={counts[item.key] || 0}
              onClick={() => {
                prepareNewBookingSound();
                setTab(item.key);
              }}
            />
          ))}
        </div>
      </div>

      {logoutItem && (
        <NavButton
          item={logoutItem}
          active={tab}
          count={0}
          onClick={() => {
            prepareNewBookingSound();
            logout();
          }}
        />
      )}

      {isOpen && (
        <button
          className="sidebar-close-btn"
          onClick={onClose}
          aria-label="Close sidebar"
          title="Close sidebar"
          type="button"
        >
          <FaChevronLeft aria-hidden="true" />
        </button>
      )}
    </nav>
  );
});

Sidebar.displayName = 'Sidebar';
export default Sidebar;
